import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach CSRF token from cookie on every mutating request
api.interceptors.request.use((config) => {
  const csrfToken = document.cookie
    .split('; ')
    .find((row) => row.startsWith('csrftoken='))
    ?.split('=')[1];
  if (csrfToken) {
    config.headers['X-CSRFToken'] = csrfToken;
  }
  return config;
});

// Response interceptor — redirect to /login on 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const { pathname } = window.location;
      // Don't redirect from pages that are intentionally public
      const isPublicPage = pathname === '/' || pathname.startsWith('/login') || pathname.startsWith('/signup') || pathname.startsWith('/confirm-email') || pathname.startsWith('/forgot-password') || pathname.startsWith('/reset-password') || pathname.startsWith('/imprint') || pathname.startsWith('/privacy') || pathname.startsWith('/sdk-auth');
      if (!isPublicPage) {
        window.location.href = '/login';
      }
    }
    console.error('API Error:', error);
    return Promise.reject(error);
  }
);

// API functions
export const apiService = {
  // Dashboard
  getDashboard: () => api.get('/dashboard/'),

  // Analyses
  getAnalyses: () => api.get('/analyses/'),
  getAnalysisQuota: () => api.get('/analyses/quota/'),
  getAnalysis: (id) => api.get(`/analyses/${id}/`),
  createAnalysis: (data) => api.post('/analyses/', data),
  updateAnalysis: (id, data) => api.patch(`/analyses/${id}/`, data),
  deleteAnalysis: (id) => api.delete(`/analyses/${id}/`),
  generateAnalysisName: (id, userContext) => api.post(`/analyses/${id}/generate-name/`, { user_context: userContext }),
  generateUploadInstruction: (id, userContext) => api.post(`/analyses/${id}/generate-upload-instruction/`, { user_context: userContext }),
  getArtifacts: (analysisId) => api.get(`/analyses/${analysisId}/artifacts/`),
  exportNotebook: (analysisId) => api.get(`/analyses/${analysisId}/export/notebook/`, { responseType: 'blob' }),

  // Datasets
  getDatasets: (analysisId) => api.get(`/analyses/${analysisId}/datasets/`),
  getDataset: (id) => api.get(`/datasets/${id}/`),
  getDatasetPreview: (id) => api.get(`/datasets/${id}/preview/`),
  getDatasetWindow: (id, { offset = 0, limit = 100 } = {}) => api.get(
    `/datasets/${id}/preview/`,
    { params: { offset, limit, full: 1 } },
  ),
  createDataset: (analysisId, data) => api.post(`/analyses/${analysisId}/datasets/`, data),
  uploadDataset: (analysisId, formData) => api.post(`/analyses/${analysisId}/datasets/`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateDataset: (id, data) => api.patch(`/datasets/${id}/`, data),
  deleteDataset: (id) => api.delete(`/datasets/${id}/`),
  getUserContext: (id) => api.get(`/analyses/${id}/dataset-user-context/`),
  setUserContext: (id, userContext) => api.patch(`/analyses/${id}/dataset-user-context/`, { user_context: userContext }),
  getAnalysisStatus: (id) => api.get(`/datasets/${id}/analysis-status/`),
  analyzeDataset: (id) => api.post(`/datasets/${id}/analyze/`),
  getTargetSelection: (id) => api.get(`/datasets/${id}/target-selection/`),
  getTargetSelectionPersist: (id) => api.get(`/datasets/${id}/target-selection-persist/`),
  setTargetSelectionPersist: (id, targetSelection) => api.patch(`/datasets/${id}/target-selection-persist/`, { target_selection: targetSelection }),

  // Modeling
  getAllTargetsModelStatus: (id) => api.get(`/datasets/${id}/all-targets-model-status/`),
  getModelStatus: (id, targetColumn) => api.get(`/datasets/${id}/model-status/`, { params: { target_column: targetColumn } }),
  deleteModel: (id) => api.delete(`/datasets/${id}/model/`),
  trainPreliminaryModel: (id, targetColumn) => api.post(`/datasets/${id}/train-preliminary-model/`, { target_column: targetColumn }),
  trainFullModel: (id, targetColumn) => api.post(`/datasets/${id}/train-full-model/`, { target_column: targetColumn }),

  // Inference
  getUploadedFiles: (id, type, targetColumn) => {
    const params = { type };
    if (targetColumn) {
      params.target_column = targetColumn;
    }
    return api.get(`/datasets/${id}/uploaded-files/`, { params });
  },
  getInferenceStatus: (id, targetColumn) => api.get(`/datasets/${id}/inference-status/`, { params: { target_column: targetColumn } }),
  uploadInferenceDataset: (id, formData) => api.post(`/datasets/${id}/upload-inference/`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  runInference: (id, targetColumn) => api.post(`/datasets/${id}/run-inference/`, { target_column: targetColumn }),

  // Explainability
  getShapStatus: (id, targetColumn) => api.get(`/datasets/${id}/shap-status/`, { params: { target_column: targetColumn } }),
  calculateShap: (id, targetColumn) => api.post(`/datasets/${id}/calculate-shap/`, { target_column: targetColumn }),

  // Actionable Insights
  getInsightsStatus: (id, targetColumn) => api.get(`/datasets/${id}/insights-status/`, { params: { target_column: targetColumn } }),
  generateInsights: (id, targetColumn) => api.post(`/datasets/${id}/generate-insights/`, { target_column: targetColumn }),

  // Re-computation
  refreshAnalysisArtifacts: (id) => api.post(`/datasets/${id}/refresh-analysis/`),
  refreshTargetSelectionArtifacts: (id) => api.post(`/datasets/${id}/refresh-target-selection/`),
  refreshModelingArtifacts: (id, targetColumn) => api.post(`/datasets/${id}/refresh-modeling/`, { target_column: targetColumn }),
  refreshExplainabilityArtifacts: (id, targetColumn) => api.post(`/datasets/${id}/refresh-explainability/`, { target_column: targetColumn }),
  refreshInsightsArtifacts: (id, targetColumn) => api.post(`/datasets/${id}/refresh-insights/`, { target_column: targetColumn }),
  getArtifactStatus: (id, page) => api.get(`/datasets/${id}/artifact-status/${page}/`),

  // Analysis chat (legacy, single-turn JSON)
  getAnalysisChat: (analysisId) => api.get(`/analyses/${analysisId}/chat/`),
  sendAnalysisChat: (analysisId, message) => api.post(`/analyses/${analysisId}/chat/`, { message }),

  // Chat-first agent (Chunk A+B)
  getAgentChatHistory: (analysisId) => api.get(`/analyses/${analysisId}/chat/agent/`),
  uploadAndClean: (analysisId, files, userContext) => {
    const fd = new FormData();
    const list = Array.isArray(files) ? files : [files];
    list.forEach(f => fd.append('files', f));
    if (userContext) fd.append('user_context', userContext);
    return api.post(`/analyses/${analysisId}/upload-and-clean/`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  // Warehouse connections (Snowflake / BigQuery / Databricks / Redshift)
  listConnections: () => api.get('/connections/'),
  getWarehouseEgressIps: () => api.get('/connections/egress-ips/'),
  createConnection: (data) => api.post('/connections/', data),
  deleteConnection: (id) => api.delete(`/connections/${id}/`),
  testConnection: (id) => api.post(`/connections/${id}/test/`),
  refreshConnectionCatalog: (id) => api.post(`/connections/${id}/refresh-catalog/`),
  getConnectionSchemas: (id, refresh) => api.get(`/connections/${id}/schemas/`, { params: refresh ? { refresh: 1 } : {} }),
  getConnectionTables: (id, schema) => api.get(`/connections/${id}/tables/`, { params: { schema } }),
  getConnectionTableSchema: (id, schema, table) => api.get(`/connections/${id}/table-schema/`, { params: { schema, table } }),
  previewWarehouse: (id, body) => api.post(`/connections/${id}/preview/`, body),
  importFromWarehouse: (analysisId, body) => api.post(`/analyses/${analysisId}/import-from-warehouse/`, body),

  // Sharing
  getAnalysisShares: (analysisId) => api.get(`/analyses/${analysisId}/shares/`),
  shareAnalysis: (analysisId, data) => api.post(`/analyses/${analysisId}/shares/`, data),
  updateAnalysisShare: (analysisId, shareId, data) => api.patch(`/analyses/${analysisId}/shares/${shareId}/`, data),
  revokeAnalysisShare: (analysisId, shareId) => api.delete(`/analyses/${analysisId}/shares/${shareId}/`),

  // Recurring pipeline runs (Manage Pipeline)
  getPipelineSchedule: (analysisId) => api.get(`/analyses/${analysisId}/pipeline-schedule/`),
  savePipelineSchedule: (analysisId, data) => api.put(`/analyses/${analysisId}/pipeline-schedule/`, data),
  runPipelineNow: (analysisId) => api.post(`/analyses/${analysisId}/pipeline-schedule/run-now/`),
  getPipelineRuns: (analysisId) => api.get(`/analyses/${analysisId}/pipeline-schedule/runs/`),

  // Preprocessing (multi-file upload)
  getRawUploads: (analysisId) => api.get(`/analyses/${analysisId}/raw-uploads/`),
  uploadRawFile: (analysisId, formData) => api.post(`/analyses/${analysisId}/raw-uploads/`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteRawUpload: (uploadId) => api.delete(`/raw-uploads/${uploadId}/`),
  getPreprocessingSession: (analysisId) => api.get(`/analyses/${analysisId}/preprocessing/`),
  runPreprocessing: (analysisId) => api.post(`/analyses/${analysisId}/preprocessing/run/`),
  getPreprocessingStatus: (analysisId) => api.get(`/analyses/${analysisId}/preprocessing/status/`),

  // Auth
  login: (email, password) => api.post('/account/login/', { email, password }),
  // qombra SDK browser login: mint a single-use PKCE auth code (session-auth'd)
  sdkApprove: (data) => api.post('/sdk/auth/approve/', data),
  register: (data) => api.post('/account/register/', data),
  confirmEmail: (token) => api.post('/account/confirm-email/', { token }),
  forgotPassword: (email) => api.post('/account/forgot-password/', { email }),
  resetPassword: (token, new_password) => api.post('/account/reset-password/', { token, new_password }),
  changePassword: (current_password, new_password) => api.post('/account/change-password/', { current_password, new_password }),

  // Account
  getAccount: () => api.get('/account/'),
  updateProfile: (formData) => api.patch('/account/profile/', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateOrganization: (formData) => api.patch('/account/organization/', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteAccount: () => api.delete('/account/delete/'),

  // Production API keys (only available to accounts enabled for production).
  listApiKeys: () => api.get('/account/api-keys/'),
  createApiKey: (name) => api.post('/account/api-keys/', { name }),
  revokeApiKey: (keyId) => api.delete(`/account/api-keys/${keyId}/`),
  logout: () => api.post('/account/logout/'),

  // Staff-only customer usage dashboard (/app/application-analytics)
  qaEntities: (q) => api.get('/application-analytics/entities/', { params: { q } }),
  qaOverview: (params) => api.get('/application-analytics/overview/', { params }),
  qaOrganization: (id, params) => api.get(`/application-analytics/organizations/${id}/`, { params }),
  qaOrganizationRuns: (id, params) => api.get(`/application-analytics/organizations/${id}/runs/`, { params }),
  qaUser: (id, params) => api.get(`/application-analytics/users/${id}/`, { params }),
  qaUserRuns: (id, params) => api.get(`/application-analytics/users/${id}/runs/`, { params }),
};

export default api;